import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { getWorkproductsHierarchy, getWorkproducts } from "@/api/api";
import { WorkProductsApiResponse, WorkProductsResponse, WorkProductActiveList, getWorkproductsActiveList } from "@/api";

import type { RootState } from "../store";

// Define the state type
interface WorkProductsState {
    workProductTypes: WorkProductsApiResponse[]; // Work products
    workProducts: WorkProductsResponse[]; // Work products
    activeList: WorkProductActiveList[]; // Active list
    contents: string[];
    sectionHeader: string;
    type_status: "idle" | "loading" | "success" | "failed";
    type_error: string | null;
    product_status: "idle" | "loading" | "success" | "failed";
    product_error: string | null;
    active_list_status: "idle" | "loading" | "success" | "failed";
    active_list_error: string | null;
}

// Initial state
const initialState: WorkProductsState = {
    workProductTypes: [],
    workProducts: [],
    activeList: [],
    contents: ["<p>Default content</p>"],
    sectionHeader: "Section Header",
    type_status: "idle",
    type_error: null,
    product_status: "idle",
    product_error: null,
    active_list_status: "idle",
    active_list_error: null
};

// Async thunk to fetch all workproduct types
export const fetchWorkproductTypes = createAsyncThunk<
    WorkProductsApiResponse[], // Return type should be an array
    void,
    { rejectValue: string }
>("workproducts/fetchWorkproductTypes", async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState() as RootState;
        const token = state.clientToken.token ?? "";

        if (!token) {
            return rejectWithValue("No token available");
        }

        try {
            const response = await getWorkproductsHierarchy(token);
            return response; // Directly return the array
        } catch (error: any) {
            console.log("Error in fetchWorkproducts:", error); // Debugging line
            return rejectWithValue(error.message || "Failed to fetch workproducts");
        }
    } catch (error: any) {
        console.error("Error fetching workproduct types:", error);
        return rejectWithValue(error.message || "Failed to fetch workproducts");
    }
});

export const fetchWorkProductActiveList = createAsyncThunk<WorkProductActiveList[], void, { rejectValue: string }>(
    "workproducts/fetchWorkProductActiveList",
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as RootState;
            const token = state.clientToken.token;
            const userid = state.userProfiles.userProfile?.userid;

            if (!token) {
                return rejectWithValue("No token available");
            }

            if (!userid) {
                return rejectWithValue("No user ID available");
            }

            const response = await getWorkproductsActiveList({ userid }, token);
            // Ensure the response is of type WorkProductActiveList[]
            return response;
        } catch (error: any) {
            console.error("Error fetching workproduct active list:", error);
            return rejectWithValue(error.message || "Failed to fetch workproducts");
        }
    }
);

// Async thunk to fetch all workproducts
// export const fetchWorkproducts = createAsyncThunk<
//     WorkProductsResponse[], // Return type should be an array
//     void,
//     { rejectValue: string }
// >("workproducts/fetchWorkproducts", async (_, { getState, rejectWithValue }) => {
//     const token = "123";
//     const projectid = 81;
//     if (!token) {
//         return rejectWithValue("No token available");
//     }
//     try {
//         const response = await getWorkproducts({ projectid }, token);
//         return response; // Directly return the array
//     } catch (error: any) {
//         console.error("Error fetching workproducts:", error);
//         return rejectWithValue(error.message || "Failed to fetch workproducts");
//     }
// });
// Create slice
const workProductsSlice = createSlice({
    name: "workProducts",
    initialState,
    reducers: {
        updateContent: (state, action: PayloadAction<string[]>) => {
            state.contents = action.payload;
        },
        updateHeader: (state, action: PayloadAction<string>) => {
            state.sectionHeader = action.payload;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchWorkproductTypes.pending, state => {
                state.type_status = "loading";
                state.type_error = null;
            })
            .addCase(fetchWorkproductTypes.fulfilled, (state, action: PayloadAction<WorkProductsApiResponse[]>) => {
                state.type_status = "success";
                state.type_error = null;
                state.workProductTypes = action.payload; // Assign payload directly
            })
            .addCase(fetchWorkproductTypes.rejected, (state, action) => {
                state.type_status = "failed";
                state.type_error = action.payload || "An error occurred while fetching workproducts";
            })
            // Fetch Work Products
            // .addCase(fetchWorkproducts.pending, state => {
            //     state.product_status = "loading";
            //     state.product_error = null;
            // })
            // .addCase(fetchWorkproducts.fulfilled, (state, action: PayloadAction<WorkProductsResponse[]>) => {
            //     state.product_status = "success";
            //     state.product_error = null;
            //     state.workProducts = action.payload; // Assign payload directly
            // })
            // .addCase(fetchWorkproducts.rejected, (state, action) => {
            //     state.product_status = "failed";
            //     state.product_error = action.payload || "An error occurred while fetching workproducts";
            // })
            // Fetch Work Product Active List
            .addCase(fetchWorkProductActiveList.pending, state => {
                state.active_list_status = "loading";
                state.active_list_error = null;
            })
            .addCase(fetchWorkProductActiveList.fulfilled, (state, action: PayloadAction<WorkProductActiveList[]>) => {
                state.active_list_status = "success";
                state.active_list_error = null;
                state.activeList = action.payload; // Assign payload directly
            })
            .addCase(fetchWorkProductActiveList.rejected, (state, action) => {
                state.active_list_status = "failed";
                state.active_list_error = action.payload || "An error occurred while fetching active list";
            });
    }
});

// Export actions and reducer
export const { updateContent, updateHeader } = workProductsSlice.actions;
export default workProductsSlice.reducer;
